import { AmountUnit, ConsignmentItemFormValues, PackagingType } from 'features/declaration/consignment-item/models/schema'
import { excludeDeleted, parseStringToNumber, sortBySequenceNumber } from 'shared/lib/common-util'
import {
  parseActiveBorderTransportMeansResponse,
} from 'features/declaration/consignment/models/mapper'
import {
  parseTransportEquipmentResponse,
  toCreateOrUpdateTransportEquipmentRequest,
} from 'features/declaration/transport-equipment/models/mapper'
import {
  toCreateOrUpdateActiveBorderTransportMeansRequest,
} from 'features/declaration/house-consignment/models/mapper'
import { ConsignmentItemResponseV3 } from '../../../../api/models/consignment-item-response-v3'
import {
  ConsignmentItem, ConsignmentItemDetails, BulkUploadItemsResult,
} from './types'
import { ConsignmentItemDetailsResponseV3 } from '../../../../api/models/consignment-item-details-response-v3'
import {
  parseAdditionalSupplyChainActorResponse,
  toCreateOrUpdateAdditionalSupplyChainActorRequest,
} from '../../additional-supply-chain-actor/models/mapper'
import { fromConsignmentDetailDetailedResponse } from '../../consignment-detail/models/mapper'
import { ConsignmentItemCreate, ConsignmentItemPatch, ConsignmentItemUpdate } from './request'
import {
  CreateOrUpdateConsignmentItemRequest,
  CreateOrUpdatePackagingRequest,
  PackagingResponse,
} from '../../../../api/models'
import { PatchConsignmentItemRequest } from '../../../../api/models/patch-consignment-item-request'
import { AdditionalReference } from '../../additional-reference/models/types'
import { toAdditionalReferenceFormValues } from '../../additional-reference/models/mapper'
import { BulkUploadResponseV3 } from '../../../../api/models/bulk-upload-response-v3'
import { Document } from '../../document/models/types'
import { toPreviousDocuments, toSupportingDocuments, toTransportDocuments } from '../../document/models/modelToModel'

export function toConsignmentItemFormValues(
  isEns: boolean,
  consignmentItem: ConsignmentItemDetails,
  documents: Document[],
  additionalReferences: AdditionalReference[],
): ConsignmentItemFormValues {
  return {
    id: consignmentItem.id,
    sequenceNumber: consignmentItem.sequenceNumber,
    goodsMeasureGrossMass: consignmentItem.goodsMeasureGrossMass,
    goodsMeasureNetMass: consignmentItem.goodsMeasureNetMass,
    declarationGoodsItemNumber: 0,
    commodityHarmonizedSystemSubHeadingCode: consignmentItem.commodityHarmonizedSystemSubHeadingCode,
    commodityCombinedNomenclatureCode: consignmentItem.commodityCombinedNomenclatureCode,
    commodityDescriptionOfGoods: consignmentItem.commodityDescriptionOfGoods,
    countryOfDispatch: consignmentItem.countryOfDispatch,
    countryOfDestination: consignmentItem.countryOfDestination,
    additionalReference: additionalReferences.map((
      additionalReference,
    ) => toAdditionalReferenceFormValues(additionalReference)),
    packaging: consignmentItem.packaging,
    referenceNumberUCR: consignmentItem.referenceNumberUCR,
    dutyAmount: consignmentItem.dutyAmount,
    dutyRate: consignmentItem.dutyRate,
    dutyRateUnit: consignmentItem.dutyRateUnit,
    vatRate: consignmentItem.vatRate,
    consignee: null,
    price: consignmentItem.price,
    excise: consignmentItem.excise,
    initialPrice: consignmentItem.initialPrice,
    initialPriceCurrency: consignmentItem.initialPriceCurrency ?? 'EUR',
    containerIndicator: consignmentItem.containerIndicator ? 'true' : 'false',
    passiveBorderTransportMeans: consignmentItem.passiveBorderTransportMeans,
    differentPassiveBorderTransportMeans: isEns ? consignmentItem.differentPassiveBorderTransportMeans : true,
    transportDocument: toTransportDocuments(documents),
    previousDocument: toPreviousDocuments(documents),
    supportingDocument: toSupportingDocuments(documents),
    transportEquipment: consignmentItem.transportEquipment,
    additionalInformation: [],
    additionalSupplyChainActor: consignmentItem.additionalSupplyChainActor,
    dangerousGoods: [],
    deleted: false,
  }
}

export function fromConsignmentItemResponseV3(response: ConsignmentItemResponseV3, index: number): ConsignmentItem {
  return {
    id: response.id,
    sequenceNumber: response.goodsItemNumber ?? index,
    commodityCombinedNomenclatureCode: response.combinedNomenclatureCode,
    commodityDescriptionOfGoods: response.descriptionOfGoods,
    commodityHarmonizedSystemSubHeadingCode: response.harmonizedSystemSubHeadingCode,
    goodsMeasureGrossMass: response.grossMass ?? 0,
    goodsMeasureNetMass: response.netMass ?? 0,
    countryOfDispatch: response.countryOfDispatch,
    countryOfDestination: response.countryOfDestination,
    totalNumberOfPackages: response.totalNumberOfPackages ?? 0,
    packagingCount: response.packagingCount ?? 0,
    invalidCommodityCode: response.invalidCommodityCode,
  }
}

export function toCreateOrUpdatePackagingRequest(
  packaging: PackagingType,
  index: number,
): CreateOrUpdatePackagingRequest {
  return {
    id: packaging.id ?? null,
    numberOfPackages: packaging.numberOfPackages,
    sequenceNumber: index,
    shippingMarks: packaging.shippingMarks,
    typeOfPackages: packaging.typeOfPackages,
  }
}

export function parsePackagingResponse(
  response: PackagingResponse | null,
  index: number,
): PackagingType {
  return {
    deleted: false,
    id: response?.id ?? null,
    numberOfPackages: response?.numberOfPackages ?? null,
    sequenceNumber: response?.sequenceNumber ?? index,
    shippingMarks: response?.shippingMarks ?? '',
    typeOfPackages: response?.typeOfPackages ?? '',
  }
}

export function fromConsignmentItemDetailsResponseV3(response: ConsignmentItemDetailsResponseV3): ConsignmentItemDetails {
  return {
    id: response.id,
    commodityCombinedNomenclatureCode: response?.combinedNomenclatureCode ?? '',
    commodityDescriptionOfGoods: response?.descriptionOfGoods ?? '',
    commodityHarmonizedSystemSubHeadingCode: response?.harmonizedSystemSubHeadingCode ?? '',
    consignee: response.consignee ? fromConsignmentDetailDetailedResponse(response.consignee, 'CONSIGNEE') : null,
    countryOfDispatch: response?.countryOfDispatch ?? '',
    countryOfDestination: response?.countryOfDestination ?? '',
    goodsMeasureGrossMass: response.grossMass ?? 0,
    goodsMeasureNetMass: response.netMass ?? 0,
    packaging: response?.packages?.map(parsePackagingResponse).sort(sortBySequenceNumber) ?? [],
    referenceNumberUCR: response.referenceNumberUcr ?? '',
    sequenceNumber: response.goodsItemNumber,
    vatRate: parseStringToNumber(response?.vatRate) ?? null,
    dutyRate: parseStringToNumber(response?.dutyRate) ?? null,
    dutyAmount: parseStringToNumber(response?.dutyAmount) ?? null,
    dutyRateUnit: parseStringToNumber(response?.dutyAmount) != null ? AmountUnit.AMOUNT : AmountUnit.PERCENTAGE,
    price: parseStringToNumber(response?.price) ?? null,
    excise: parseStringToNumber(response?.excise) ?? null,
    initialPrice: parseStringToNumber(response?.price) ?? null,
    initialPriceCurrency: 'EUR',
    transportEquipment: response?.transportEquipments?.map(parseTransportEquipmentResponse) ?? [],
    passiveBorderTransportMeans: response?.passiveBorderTransportMeans?.map(parseActiveBorderTransportMeansResponse) ?? [],
    additionalSupplyChainActor: response?.additionalSupplyChainActors?.map(parseAdditionalSupplyChainActorResponse) ?? [],
    containerIndicator: false,
    differentPassiveBorderTransportMeans: response?.differentPassiveBorderTransportMeans === true,
  }
}

export function toConsignmentItemCreate(
  houseConsignmentId: number,
  formValues: ConsignmentItemFormValues,
): ConsignmentItemCreate {
  return {
    houseConsignmentId,
    sequenceNumber: formValues.sequenceNumber,
    goodsMeasureGrossMass: formValues.goodsMeasureGrossMass,
    goodsMeasureNetMass: formValues.goodsMeasureNetMass,
    commodityHarmonizedSystemSubHeadingCode: formValues.commodityHarmonizedSystemSubHeadingCode,
    commodityCombinedNomenclatureCode: formValues.commodityCombinedNomenclatureCode,
    commodityDescriptionOfGoods: formValues.commodityDescriptionOfGoods,
    countryOfDispatch: formValues.countryOfDispatch,
    countryOfDestination: formValues.countryOfDestination,
    differentPassiveBorderTransportMeans: formValues.differentPassiveBorderTransportMeans,
    packaging: formValues.packaging,
    referenceNumberUCR: formValues.referenceNumberUCR,
    dutyAmount: formValues.dutyAmount,
    dutyRate: formValues.dutyRate,
    dutyRateUnit: formValues.dutyRateUnit,
    vatRate: formValues.vatRate,
    consignee: null,
    price: formValues.price,
    excise: formValues.excise,
    containerIndicator: formValues.containerIndicator === 'true',
    passiveBorderTransportMeans: formValues.passiveBorderTransportMeans,
    transportEquipment: formValues.transportEquipment,
    additionalSupplyChainActor: formValues.additionalSupplyChainActor,
  }
}

export function toConsignmentItemCreateDraft(
  houseConsignmentId: number,
  sequenceNumber: number,
): ConsignmentItemCreate {
  return {
    houseConsignmentId,
    sequenceNumber,
    goodsMeasureGrossMass: 0,
    goodsMeasureNetMass: 0,
    commodityHarmonizedSystemSubHeadingCode: '',
    commodityCombinedNomenclatureCode: '',
    commodityDescriptionOfGoods: '',
    countryOfDispatch: '',
    countryOfDestination: '',
    differentPassiveBorderTransportMeans: false,
    packaging: [],
    referenceNumberUCR: '',
    dutyAmount: null,
    dutyRate: null,
    dutyRateUnit: null,
    vatRate: null,
    consignee: null,
    price: null,
    excise: null,
    containerIndicator: false,
    passiveBorderTransportMeans: [],
    transportEquipment: [],
    additionalSupplyChainActor: [],
  }
}

export function toConsignmentItemUpdate(
  houseConsignmentId: number,
  formValues: ConsignmentItemFormValues,
): ConsignmentItemUpdate {
  return {
    houseConsignmentId,
    sequenceNumber: formValues.sequenceNumber,
    goodsMeasureGrossMass: formValues.goodsMeasureGrossMass,
    goodsMeasureNetMass: formValues.goodsMeasureNetMass,
    commodityHarmonizedSystemSubHeadingCode: formValues.commodityHarmonizedSystemSubHeadingCode,
    commodityCombinedNomenclatureCode: formValues.commodityCombinedNomenclatureCode,
    commodityDescriptionOfGoods: formValues.commodityDescriptionOfGoods,
    countryOfDispatch: formValues.countryOfDispatch,
    countryOfDestination: formValues.countryOfDestination,
    differentPassiveBorderTransportMeans: formValues.differentPassiveBorderTransportMeans,
    packaging: formValues.packaging,
    referenceNumberUCR: formValues.referenceNumberUCR,
    dutyAmount: formValues.dutyAmount,
    dutyRate: formValues.dutyRate,
    dutyRateUnit: formValues.dutyRateUnit,
    vatRate: formValues.vatRate,
    consignee: null,
    price: formValues.price,
    excise: formValues.excise,
    containerIndicator: formValues.containerIndicator === 'true',
    passiveBorderTransportMeans: formValues.passiveBorderTransportMeans,
    transportEquipment: formValues.transportEquipment,
    additionalSupplyChainActor: formValues.additionalSupplyChainActor,
  }
}

export function toCreateConsignmentItemRequest(
  data: ConsignmentItemCreate,
): CreateOrUpdateConsignmentItemRequest {
  data.packaging.sort(sortBySequenceNumber)

  return {
    combinedNomenclatureCode: data.commodityCombinedNomenclatureCode,
    consigneeId: null,
    countryOfDispatch: data.countryOfDispatch === 'COUNTRY_' ? '' : data.countryOfDispatch,
    countryOfDestination: data.countryOfDestination === 'COUNTRY_' ? '' : data.countryOfDestination,
    declarationGoodsItemNumber: 0,
    descriptionOfGoods: data.commodityDescriptionOfGoods,
    grossMass: data.goodsMeasureGrossMass,
    harmonizedSystemSubHeadingCode: data.commodityHarmonizedSystemSubHeadingCode,
    netMass: data.goodsMeasureNetMass,
    packages: data.packaging
      .filter(excludeDeleted)
      .map(toCreateOrUpdatePackagingRequest),
    referenceNumberUcr: data.referenceNumberUCR,
    goodsItemNumber: data.sequenceNumber,
    houseConsignmentId: data.houseConsignmentId,
    vatRate: (data.vatRate?.toString() ?? null),
    // eslint-disable-next-line no-nested-ternary
    dutyRate: data.dutyRateUnit === 'PERCENTAGE' ? (!Number.isNaN(data.dutyRate) ? data.dutyRate?.toString() ?? null : null) : null,
    // eslint-disable-next-line no-nested-ternary
    dutyAmount: data.dutyRateUnit === 'AMOUNT' ? (!Number.isNaN(data.dutyAmount) ? data.dutyAmount?.toString() ?? null : null) : null,
    price: data.price?.toString() ?? null,
    excise: data.excise?.toString() ?? null,
    transportEquipments: data.transportEquipment
      .filter(excludeDeleted)
      .map((equipment, index) => toCreateOrUpdateTransportEquipmentRequest(equipment, index)),
    passiveBorderTransportMeans: data.passiveBorderTransportMeans
      .filter(excludeDeleted)
      .map(toCreateOrUpdateActiveBorderTransportMeansRequest),
    differentPassiveBorderTransportMeans: data.differentPassiveBorderTransportMeans,
    additionalSupplyChainActors: data.additionalSupplyChainActor
      .filter(excludeDeleted)
      .map(toCreateOrUpdateAdditionalSupplyChainActorRequest),
  }
}

export function toUpdateConsignmentItemRequest(
  data: ConsignmentItemUpdate,
): CreateOrUpdateConsignmentItemRequest {
  data.packaging.sort(sortBySequenceNumber)

  return {
    combinedNomenclatureCode: data.commodityCombinedNomenclatureCode,
    consigneeId: null,
    countryOfDispatch: data.countryOfDispatch === 'COUNTRY_' ? '' : data.countryOfDispatch,
    countryOfDestination: data.countryOfDestination === 'COUNTRY_' ? '' : data.countryOfDestination,
    declarationGoodsItemNumber: 0,
    descriptionOfGoods: data.commodityDescriptionOfGoods,
    grossMass: data.goodsMeasureGrossMass,
    harmonizedSystemSubHeadingCode: data.commodityHarmonizedSystemSubHeadingCode,
    netMass: data.goodsMeasureNetMass,
    packages: data.packaging
      .filter(excludeDeleted)
      .map(toCreateOrUpdatePackagingRequest),
    referenceNumberUcr: data.referenceNumberUCR,
    goodsItemNumber: data.sequenceNumber,
    houseConsignmentId: data.houseConsignmentId,
    vatRate: (data.vatRate?.toString() ?? null),
    // eslint-disable-next-line no-nested-ternary
    dutyRate: data.dutyRateUnit === 'PERCENTAGE' ? (!Number.isNaN(data.dutyRate) ? data.dutyRate?.toString() ?? null : null) : null,
    // eslint-disable-next-line no-nested-ternary
    dutyAmount: data.dutyRateUnit === 'AMOUNT' ? (!Number.isNaN(data.dutyAmount) ? data.dutyAmount?.toString() ?? null : null) : null,
    price: data.price?.toString() ?? null,
    excise: data.excise?.toString() ?? null,
    transportEquipments: data.transportEquipment
      .filter(excludeDeleted)
      .map((equipment, index) => toCreateOrUpdateTransportEquipmentRequest(equipment, index)),
    passiveBorderTransportMeans: data.passiveBorderTransportMeans
      .filter(excludeDeleted)
      .map(toCreateOrUpdateActiveBorderTransportMeansRequest),
    differentPassiveBorderTransportMeans: data.differentPassiveBorderTransportMeans,
    additionalSupplyChainActors: data.additionalSupplyChainActor
      .filter(excludeDeleted)
      .map(toCreateOrUpdateAdditionalSupplyChainActorRequest),
  }
}

export function toPatchConsignmentItemRequest(patch: ConsignmentItemPatch): PatchConsignmentItemRequest {
  return {
    descriptionOfGoods: patch.commodityDescriptionOfGoods,
    harmonizedSystemSubHeadingCode: patch.commodityHarmonizedSystemSubHeadingCode,
    combinedNomenclatureCode: patch.commodityCombinedNomenclatureCode,
    grossMass: patch.goodsMeasureGrossMass ?? undefined,
    netMass: patch.goodsMeasureNetMass ?? undefined,
    totalNumberOfPackages: patch.totalNumberOfPackages ?? undefined,
  }
}

export function fromConsignmentItemDetails(details: ConsignmentItemDetails): ConsignmentItem {
  return {
    id: details.id,
    sequenceNumber: details.sequenceNumber,
    commodityDescriptionOfGoods: details.commodityDescriptionOfGoods,
    commodityHarmonizedSystemSubHeadingCode: details.commodityHarmonizedSystemSubHeadingCode,
    commodityCombinedNomenclatureCode: details.commodityCombinedNomenclatureCode,
    countryOfDispatch: details.countryOfDispatch,
    countryOfDestination: details.countryOfDestination,
    goodsMeasureGrossMass: details.goodsMeasureGrossMass,
    goodsMeasureNetMass: details.goodsMeasureNetMass,
    totalNumberOfPackages: details.packaging.reduce((sum, packaging) => {
      if (packaging.deleted) return sum
      return sum + (packaging.numberOfPackages ?? 0)
    }, 0),
    packagingCount: details.packaging.filter((packaging) => !packaging.deleted).length,
    invalidCommodityCode: false,
  }
}

export function fromBulkUploadItemResponse(response: BulkUploadResponseV3): BulkUploadItemsResult {
  return {
    errors: (response.errors ?? []).map((r) => ({
      rowNumber: r.rowNumber ?? -1,
      field: r.field ?? '',
      message: r.message ?? '',
    })),
  }
}
